/**
 * DualListPicker Component
 *
 * A two-panel list picker with search, multi-selection (checkboxes, Shift+Click, Ctrl+Click),
 * arrow controls, and keyboard navigation.
 * Left panel shows available items, right panel shows selected items.
 */

import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Input } from './input';
import { Button } from './button';
import { Checkbox } from './checkbox';
import { ScrollArea } from './scroll-area';
import {
  ChevronRight,
  ChevronLeft,
  ChevronsRight,
  ChevronsLeft,
  Search,
  Check,
} from 'lucide-react';
import { cn } from '../../lib/utils';

export type DualListItem = {
  id: string;
  name: string;
  schema?: string;
};

// F10 — end-ellipsis on a long name clips exactly the part that tells rows
// with a shared prefix apart (e.g. many `sp_tblPropiedadesDescripcion*`
// procedures all collapse to `sp_tblPropiedadesD…`). Keep the last N chars
// as a `shrink-0` tail so the distinguishing suffix always survives, and only
// let the (now shorter) head clip with CSS ellipsis when space is tight.
const MIDDLE_ELLIPSIS_TAIL_CHARS = 10;

function splitNameForMiddleEllipsis(
  name: string,
  tailChars: number = MIDDLE_ELLIPSIS_TAIL_CHARS
): { head: string; tail: string } {
  if (name.length <= tailChars) {
    return { head: name, tail: '' };
  }
  return {
    head: name.slice(0, name.length - tailChars),
    tail: name.slice(-tailChars),
  };
}

type DualListPickerProps = {
  /** All available items */
  items: DualListItem[];
  /** Currently selected item IDs (items in right panel) */
  selectedIds: string[];
  /** Callback when selection changes */
  onSelectionChange: (selectedIds: string[]) => void;
  /** Placeholder for search input */
  searchPlaceholder?: string;
  /** Height of the picker (default: 320px) */
  height?: number;
  /** Loading state */
  isLoading?: boolean;
  /**
   * When true, the Selected (right) panel also gets a search box, and the
   * "Remove all" control removes only the items matching that search. Lets the
   * user deselect a searched subset when the picker starts fully-selected.
   * Default false (existing consumers keep a single left-panel search).
   */
  selectedSearchable?: boolean;
};

export function DualListPicker({
  items,
  selectedIds,
  onSelectionChange,
  searchPlaceholder = 'Search...',
  height = 320,
  isLoading = false,
  selectedSearchable = false,
}: DualListPickerProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSearchQuery, setSelectedSearchQuery] = useState('');
  // Multi-selection state for left and right panels (checkboxes)
  const [checkedLeft, setCheckedLeft] = useState<Set<string>>(new Set());
  const [checkedRight, setCheckedRight] = useState<Set<string>>(new Set());
  // Last clicked item for shift-click range selection
  const [lastClickedLeft, setLastClickedLeft] = useState<string | null>(null);
  const [lastClickedRight, setLastClickedRight] = useState<string | null>(null);
  const leftListRef = useRef<HTMLDivElement>(null);
  const rightListRef = useRef<HTMLDivElement>(null);

  // Selected items set for fast lookup (items in right panel)
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  // Available items (not selected - left panel)
  const availableItems = useMemo(() => {
    return items.filter((item) => !selectedSet.has(item.id));
  }, [items, selectedSet]);

  // Selected items (right panel)
  const selectedItems = useMemo(() => {
    return items.filter((item) => selectedSet.has(item.id));
  }, [items, selectedSet]);

  // Filtered available items based on search
  const filteredAvailable = useMemo(() => {
    if (!searchQuery.trim()) return availableItems;
    const query = searchQuery.toLowerCase();
    return availableItems.filter(
      (item) =>
        item.name.toLowerCase().includes(query) ||
        (item.schema && item.schema.toLowerCase().includes(query))
    );
  }, [availableItems, searchQuery]);

  // Filtered selected items based on the optional right-panel search
  const filteredSelected = useMemo(() => {
    if (!selectedSearchable || !selectedSearchQuery.trim()) return selectedItems;
    const query = selectedSearchQuery.toLowerCase();
    return selectedItems.filter(
      (item) =>
        item.name.toLowerCase().includes(query) ||
        (item.schema && item.schema.toLowerCase().includes(query))
    );
  }, [selectedItems, selectedSearchable, selectedSearchQuery]);

  // Clear checked items that no longer exist in their respective lists
  useEffect(() => {
    const availableIds = new Set(filteredAvailable.map((i) => i.id));
    setCheckedLeft((prev) => {
      const newSet = new Set<string>();
      prev.forEach((id) => {
        if (availableIds.has(id)) newSet.add(id);
      });
      return newSet.size !== prev.size ? newSet : prev;
    });
  }, [filteredAvailable]);

  useEffect(() => {
    const selectedItemIds = new Set(selectedItems.map((i) => i.id));
    setCheckedRight((prev) => {
      const newSet = new Set<string>();
      prev.forEach((id) => {
        if (selectedItemIds.has(id)) newSet.add(id);
      });
      return newSet.size !== prev.size ? newSet : prev;
    });
  }, [selectedItems]);

  // Handle left panel item click with multi-select support
  const handleLeftItemClick = useCallback(
    (itemId: string, event: React.MouseEvent) => {
      const isCtrlOrCmd = event.ctrlKey || event.metaKey;
      const isShift = event.shiftKey;

      if (isShift && lastClickedLeft) {
        // Shift+Click: select range
        const currentIndex = filteredAvailable.findIndex((i) => i.id === itemId);
        const lastIndex = filteredAvailable.findIndex((i) => i.id === lastClickedLeft);
        if (currentIndex !== -1 && lastIndex !== -1) {
          const start = Math.min(currentIndex, lastIndex);
          const end = Math.max(currentIndex, lastIndex);
          const rangeIds = filteredAvailable.slice(start, end + 1).map((i) => i.id);
          setCheckedLeft((prev) => {
            const newSet = new Set(prev);
            rangeIds.forEach((id) => newSet.add(id));
            return newSet;
          });
        }
      } else if (isCtrlOrCmd) {
        // Ctrl/Cmd+Click: toggle single item
        setCheckedLeft((prev) => {
          const newSet = new Set(prev);
          if (newSet.has(itemId)) {
            newSet.delete(itemId);
          } else {
            newSet.add(itemId);
          }
          return newSet;
        });
        setLastClickedLeft(itemId);
      } else {
        // Regular click: select only this item
        setCheckedLeft(new Set([itemId]));
        setLastClickedLeft(itemId);
      }
    },
    [filteredAvailable, lastClickedLeft]
  );

  // Handle right panel item click with multi-select support
  const handleRightItemClick = useCallback(
    (itemId: string, event: React.MouseEvent) => {
      const isCtrlOrCmd = event.ctrlKey || event.metaKey;
      const isShift = event.shiftKey;

      if (isShift && lastClickedRight) {
        // Shift+Click: select range
        const currentIndex = selectedItems.findIndex((i) => i.id === itemId);
        const lastIndex = selectedItems.findIndex((i) => i.id === lastClickedRight);
        if (currentIndex !== -1 && lastIndex !== -1) {
          const start = Math.min(currentIndex, lastIndex);
          const end = Math.max(currentIndex, lastIndex);
          const rangeIds = selectedItems.slice(start, end + 1).map((i) => i.id);
          setCheckedRight((prev) => {
            const newSet = new Set(prev);
            rangeIds.forEach((id) => newSet.add(id));
            return newSet;
          });
        }
      } else if (isCtrlOrCmd) {
        // Ctrl/Cmd+Click: toggle single item
        setCheckedRight((prev) => {
          const newSet = new Set(prev);
          if (newSet.has(itemId)) {
            newSet.delete(itemId);
          } else {
            newSet.add(itemId);
          }
          return newSet;
        });
        setLastClickedRight(itemId);
      } else {
        // Regular click: select only this item
        setCheckedRight(new Set([itemId]));
        setLastClickedRight(itemId);
      }
    },
    [selectedItems, lastClickedRight]
  );

  // Toggle checkbox for left panel
  const toggleLeftCheckbox = useCallback((itemId: string) => {
    setCheckedLeft((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  }, []);

  // Toggle checkbox for right panel
  const toggleRightCheckbox = useCallback((itemId: string) => {
    setCheckedRight((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  }, []);

  // Move checked items from left to right (add to selected)
  const moveCheckedToSelected = useCallback(() => {
    if (checkedLeft.size === 0) return;
    const newIds = [...selectedIds, ...Array.from(checkedLeft)];
    onSelectionChange(newIds);
    setCheckedLeft(new Set());
  }, [checkedLeft, selectedIds, onSelectionChange]);

  // Move checked items from right to left (remove from selected)
  const moveCheckedToAvailable = useCallback(() => {
    if (checkedRight.size === 0) return;
    const newIds = selectedIds.filter((id) => !checkedRight.has(id));
    onSelectionChange(newIds);
    setCheckedRight(new Set());
  }, [checkedRight, selectedIds, onSelectionChange]);

  // Move all filtered items to selected
  const moveAllToSelected = useCallback(() => {
    const newIds = filteredAvailable.map((item) => item.id);
    const combined = [...selectedIds, ...newIds.filter((id) => !selectedSet.has(id))];
    onSelectionChange(combined);
    setSearchQuery('');
    setCheckedLeft(new Set());
  }, [filteredAvailable, selectedIds, selectedSet, onSelectionChange]);

  // Move all to available. When a right-panel search is active this removes only
  // the matching (filtered) selected items; otherwise it removes everything.
  const moveAllToAvailable = useCallback(() => {
    const removeIds = new Set(filteredSelected.map((i) => i.id));
    onSelectionChange(selectedIds.filter((id) => !removeIds.has(id)));
    setCheckedRight(new Set());
  }, [filteredSelected, selectedIds, onSelectionChange]);

  // Handle double-click to move single item
  const handleLeftDoubleClick = useCallback(
    (itemId: string) => {
      if (!selectedSet.has(itemId)) {
        onSelectionChange([...selectedIds, itemId]);
        setCheckedLeft((prev) => {
          const newSet = new Set(prev);
          newSet.delete(itemId);
          return newSet;
        });
      }
    },
    [selectedIds, selectedSet, onSelectionChange]
  );

  const handleRightDoubleClick = useCallback(
    (itemId: string) => {
      onSelectionChange(selectedIds.filter((id) => id !== itemId));
      setCheckedRight((prev) => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
    },
    [selectedIds, onSelectionChange]
  );

  // Handle keyboard navigation for left list
  const handleLeftKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (filteredAvailable.length === 0) return;

      if (e.key === 'a' && (e.ctrlKey || e.metaKey)) {
        // Ctrl/Cmd+A: select all
        e.preventDefault();
        setCheckedLeft(new Set(filteredAvailable.map((i) => i.id)));
      } else if (e.key === 'Enter' || e.key === ' ') {
        // Enter/Space: move checked items
        e.preventDefault();
        if (checkedLeft.size > 0) {
          moveCheckedToSelected();
        }
      }
    },
    [filteredAvailable, checkedLeft, moveCheckedToSelected]
  );

  // Handle keyboard navigation for right list
  const handleRightKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (selectedItems.length === 0) return;

      if (e.key === 'a' && (e.ctrlKey || e.metaKey)) {
        // Ctrl/Cmd+A: select all (matching the right-panel search, if any)
        e.preventDefault();
        setCheckedRight(new Set(filteredSelected.map((i) => i.id)));
      } else if (e.key === 'Enter' || e.key === ' ' || e.key === 'Backspace' || e.key === 'Delete') {
        // Enter/Space/Backspace/Delete: move checked items
        e.preventDefault();
        if (checkedRight.size > 0) {
          moveCheckedToAvailable();
        }
      }
    },
    [selectedItems, filteredSelected, checkedRight, moveCheckedToAvailable]
  );

  // Render item with checkbox
  const renderItem = (
    item: DualListItem,
    isChecked: boolean,
    onCheckboxToggle: () => void,
    onClick: (e: React.MouseEvent) => void,
    onDoubleClick: () => void
  ) => {
    const fullName = item.schema ? `${item.schema}.${item.name}` : item.name;
    const { head, tail } = splitNameForMiddleEllipsis(item.name);
    return (
      <div
        key={item.id}
        className={cn(
          'flex items-center gap-2 px-2 py-1.5 cursor-pointer rounded-sm text-sm min-w-0',
          'hover:bg-accent transition-colors',
          isChecked && 'bg-accent/50'
        )}
        onClick={onClick}
        onDoubleClick={onDoubleClick}
      >
        <Checkbox
          checked={isChecked}
          onCheckedChange={onCheckboxToggle}
          onClick={(e) => e.stopPropagation()}
          className="h-4 w-4"
        />
        <span className="flex min-w-0 flex-1 items-center" title={fullName}>
          <span className="truncate min-w-0">{head}</span>
          {tail && <span className="shrink-0">{tail}</span>}
        </span>
        {item.schema && (
          <span className="text-xs text-muted-foreground truncate">{item.schema}</span>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <div className="text-sm text-muted-foreground">Loading objects...</div>
      </div>
    );
  }

  return (
    <div className="flex gap-2" style={{ height }}>
      {/* Left panel - Available items */}
      <div className="flex-1 flex flex-col border border-border rounded-md overflow-hidden">
        <div className="p-2 border-b border-border bg-muted/30">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={searchPlaceholder}
              className="pl-8 h-8 text-sm"
            />
          </div>
          <div className="mt-1.5 flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              Available ({filteredAvailable.length}
              {searchQuery && filteredAvailable.length !== availableItems.length
                ? ` / ${availableItems.length}`
                : ''}
              )
            </span>
            {checkedLeft.size > 0 && (
              <span className="text-xs text-primary font-medium">
                {checkedLeft.size} selected
              </span>
            )}
          </div>
        </div>
        <ScrollArea
          className="flex-1"
          ref={leftListRef}
          tabIndex={0}
          onKeyDown={handleLeftKeyDown}
        >
          <div className="p-1">
            {filteredAvailable.length === 0 ? (
              <div className="text-center py-4 text-sm text-muted-foreground">
                {searchQuery ? 'No matches found' : 'No items available'}
              </div>
            ) : (
              filteredAvailable.map((item) =>
                renderItem(
                  item,
                  checkedLeft.has(item.id),
                  () => toggleLeftCheckbox(item.id),
                  (e) => handleLeftItemClick(item.id, e),
                  () => handleLeftDoubleClick(item.id)
                )
              )
            )}
          </div>
        </ScrollArea>
        <div className="p-1.5 border-t border-border bg-muted/20 text-xs text-muted-foreground">
          Tip: Shift+Click for range, Ctrl+Click for multi
        </div>
      </div>

      {/* Center controls */}
      <div className="flex flex-col items-center justify-center gap-1.5 px-1">
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8"
          onClick={moveAllToSelected}
          disabled={filteredAvailable.length === 0}
          title="Add all"
        >
          <ChevronsRight className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8"
          onClick={moveCheckedToSelected}
          disabled={checkedLeft.size === 0}
          title={`Add selected (${checkedLeft.size})`}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8"
          onClick={moveCheckedToAvailable}
          disabled={checkedRight.size === 0}
          title={`Remove selected (${checkedRight.size})`}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8"
          onClick={moveAllToAvailable}
          disabled={filteredSelected.length === 0}
          title={selectedSearchable && selectedSearchQuery ? `Remove all matching (${filteredSelected.length})` : 'Remove all'}
        >
          <ChevronsLeft className="h-4 w-4" />
        </Button>
      </div>

      {/* Right panel - Selected items */}
      <div className="flex-1 flex flex-col border border-border rounded-md overflow-hidden">
        <div className="p-2 border-b border-border bg-muted/30">
          {selectedSearchable && (
            <div className="relative mb-1.5">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={selectedSearchQuery}
                onChange={(e) => setSelectedSearchQuery(e.target.value)}
                placeholder={searchPlaceholder}
                className="pl-8 h-8 text-sm"
              />
            </div>
          )}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Check className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">
                Selected ({filteredSelected.length}
                {selectedSearchable && selectedSearchQuery && filteredSelected.length !== selectedItems.length
                  ? ` / ${selectedItems.length}`
                  : ''}
                )
              </span>
            </div>
            {checkedRight.size > 0 && (
              <span className="text-xs text-primary font-medium">
                {checkedRight.size} selected
              </span>
            )}
          </div>
        </div>
        <ScrollArea
          className="flex-1"
          ref={rightListRef}
          tabIndex={0}
          onKeyDown={handleRightKeyDown}
        >
          <div className="p-1">
            {filteredSelected.length === 0 ? (
              <div className="text-center py-4 text-sm text-muted-foreground">
                {selectedSearchable && selectedSearchQuery ? 'No matches found' : 'No items selected'}
              </div>
            ) : (
              filteredSelected.map((item) =>
                renderItem(
                  item,
                  checkedRight.has(item.id),
                  () => toggleRightCheckbox(item.id),
                  (e) => handleRightItemClick(item.id, e),
                  () => handleRightDoubleClick(item.id)
                )
              )
            )}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}
